import type { Course, LngLat } from './types';
import type { DirectoryCourse } from './directory';
import { apiGet } from './api';
import { holeHasGeometry } from './holeGeometry';

export { holeHasGeometry };

const CACHE_KEY = 'fairway.course.v1';

/**
 * Stand-in for "no course chosen yet".
 *
 * Deliberately empty rather than a demo layout: an invented hole drawn over
 * the golfer's real surroundings looks like a bug, and there is no honest
 * default for which of 1,449 courses somebody is standing on.
 */
export const EMPTY_COURSE: Course = {
  id: '',
  name: '',
  holes: [],
  teeColours: ['White'],
  centre: [0, 0],
  keepOut: [],
  source: 'local',
};

/**
 * Course resolution order:
 *   1. operator-surveyed course stored on the backend (the real one, once the
 *      course has been walked with Survey mode),
 *   2. GolfCourseAPI lookup proxied through the worker (keeps the key server
 *      side and lets us cache one response for the whole fleet),
 *   3. last good cached copy on the device,
 *   4. the demo layout, so a unit with no signal on day one still boots.
 */
/** Why a course has no holes — the golfer is told which, because the fixes differ. */
export type LoadFailure = 'unavailable' | 'no-holes' | 'not-mapped';

/**
 * ApiError carries the raw response body as its message, so the worker's
 * machine-readable `reason` has to be dug back out of it. An abort (the request
 * timing out client-side) has no body at all and means the import was still
 * running — which is retryable, not a missing course.
 */
function reasonOf(e: unknown): LoadFailure {
  const msg = (e as Error)?.message ?? '';
  try {
    const reason = (JSON.parse(msg) as { reason?: string }).reason;
    if (reason === 'no-holes') return 'no-holes';
    if (reason === 'no-polygon') return 'not-mapped';
  } catch {
    /* not JSON — an abort or a network failure */
  }
  return 'unavailable';
}

export async function loadCourse(
  courseId: string
): Promise<{ course: Course; live: boolean; failure?: LoadFailure }> {
  let failure: LoadFailure | undefined;
  try {
    // 90 seconds, not the usual 8. A course the backend has never seen is built
    // from OpenStreetMap on this request — three Overpass queries against a
    // rate-limited public service. The default timeout aborted every first-ever
    // load of a course, which is precisely the case this exists to serve.
    const c = await apiGet<Course>(`/api/courses/${encodeURIComponent(courseId)}`, 90_000);
    if (c && Array.isArray(c.holes) && c.holes.length) {
      // OSM hands us "ghost" holes all the time: a par number with no green or
      // tee coordinates (golf:par is tagged far more often than the geometry).
      // Accepting those on holes.length alone is exactly what pinned the yardage
      // to a fake point and stuck the app on "Hole 1 · par 4" all round. A hole
      // only counts as mapped if it has a real tee AND a real green to measure to.
      const mapped = c.holes.filter(holeHasGeometry);
      if (mapped.length) {
        const clean = mapped.length === c.holes.length ? c : { ...c, holes: mapped };
        localStorage.setItem(CACHE_KEY, JSON.stringify(clean));
        return { course: clean, live: true };
      }
    }
    failure = 'no-holes';
  } catch (e) {
    // The worker reports WHY in the 404 body. "Overpass was busy" and "OSM has
    // never mapped this course" look identical on screen otherwise, and only
    // one of them is worth retrying.
    failure = reasonOf(e);
  }

  const cached = readCache();
  if (cached && cached.id === courseId) return { course: cached, live: false };
  return { course: EMPTY_COURSE, live: false, failure };
}

function readCache(): Course | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as Course) : null;
  } catch {
    return null;
  }
}

export interface CourseSearchResult {
  id: string;
  name: string;
  city?: string;
  country?: string;
}

export async function searchCourses(q: string): Promise<CourseSearchResult[]> {
  if (q.trim().length < 3) return [];
  try {
    return await apiGet<CourseSearchResult[]>(`/api/courses?q=${encodeURIComponent(q)}`);
  } catch {
    return [];
  }
}

/**
 * A course picked from the national directory that has not been surveyed yet.
 *
 * The directory knows where a course is and how many holes it has, but nobody
 * has walked it, so there are no tee or green coordinates. Rather than invent
 * geometry and hand a golfer fabricated yardages, this returns a course with no
 * holes — the app then shows a "needs survey" state and offers Survey mode.
 */
export function provisionalCourse(d: DirectoryCourse): Course {
  return {
    id: d.id,
    name: d.name,
    city: d.suburb,
    country: 'AU',
    teeColours: ['White'],
    centre: [d.lng, d.lat] as LngLat,
    holes: [],
    keepOut: [],
    source: 'operator',
  };
}
